'use client';

/**
 * Lo stato di salute dei dati energetici di un punto di misura.
 *
 * ── Perché una scheda a sé ──────────────────────────────────────────────────
 * Il dialogo dei livelli (`PodDetailDialog`) risponde a «cosa è successo in
 * questo intervallo»: prende la finestra che stavi guardando e la scompone. La
 * domanda di qui è l'altra — «di questo punto, cosa possediamo?» — e ha una
 * finestra sola, quella della garanzia: `oggi−365 → oggi+7`. Sono due letture
 * diverse abbastanza da non stare nello stesso posto: la prima si apre da un
 * grafico e si chiude, questa si guarda prima di fidarsi del grafico.
 *
 * ── Cosa mostra, e in che ordine ────────────────────────────────────────────
 * 1. **I quattro livelli**, sempre tutti e quattro anche a zero. Un livello che
 *    non copre nulla è informazione — il GSE non ha ancora certificato il mese —
 *    e nasconderlo lo farebbe sembrare non pertinente invece che assente.
 * 2. **Le due finestre della garanzia**, separate perché hanno regole diverse:
 *    l'anno indietro si riempie solo dove manca, l'orizzonte avanti si riscrive
 *    sempre. Un buco nel secondo è un difetto più urgente del primo — vuol dire
 *    che la catena notturna non arriva in fondo — e la scheda lo dice.
 * 3. **I buchi come intervalli**, non come conteggio: «10 giorni scoperti» non
 *    si va a riparare, «dal 1 al 10 marzo» sì.
 * 4. **I rilievi**, in fondo, ciascuno con il perché.
 *
 * I colori dei livelli arrivano dalla chart e non da una tavolozza nuova: la
 * stessa banda grigia che nel grafico è il tipico deve essere grigia anche qui,
 * o le due letture non si sovrappongono nella testa di chi guarda.
 */

import { IT_INT } from '@slims/shared/constants';
import {
	AlertTriangle,
	CalendarClock,
	CheckCircle2,
	CircleSlash,
	History,
	Info,
	RefreshCw,
	XCircle,
} from 'lucide-react';
import { PodLayerColors, PodLayersChart, type PodLayerPoint } from '../../charts';
import { Button } from '../../atoms/button/button';
import { StatusBlock } from '../../molecules/status-block/status-block';

export type CurveHealthStatus = 'ok' | 'warn' | 'error' | 'exempt';
export type CurveHealthSeverity = 'error' | 'warn' | 'info';

/** Un livello sul punto di misura, in tutta la finestra della garanzia. */
export interface CurveHealthLayerItem {
	/** Rango 1–4: 1 GSE, 2 Reale, 3 Stimato, 4 Tipico. */
	rank: number;
	/** Nome del livello, come lo dichiara `@slims/shared/energy`. */
	name: 'gse' | 'reale' | 'stimato' | 'tipico';
	label: string;
	daysPast: number;
	daysFuture: number;
	/** Giorni presenti ma interamente a zero. */
	daysZero: number;
	kwh: number;
	slotsFilled: number;
	slotsTotal: number;
	firstDay: string | null;
	lastDay: string | null;
	lastWriteAt: string | null;
	origins: { code: number; label: string; slots: number }[];
}

/** Un intervallo continuo di giorni senza la coppia garantita. */
export interface CurveHealthGapItem {
	from: string;
	/** Ultimo giorno scoperto, incluso. */
	to: string;
	days: number;
}

export interface CurveHealthIssueItem {
	severity: CurveHealthSeverity;
	code: string;
	message: string;
}

export interface CurveHealthMeterItem {
	meterId: string;
	/** M1 prelievo · M2 immissione · M3 accumulo. */
	kind: string;
	tipo: string;
	podCodice: string | null;
	status: CurveHealthStatus;
	/** Falso sui punti di accumulo, che la garanzia non riguarda. */
	guaranteed: boolean;
	issues: CurveHealthIssueItem[];
	profile: { codice: string; basis: string; updatedAt: string; hasCoefficients?: boolean } | null;
	stale: boolean;
	past: { days: number; covered: number; missing: number };
	future: { days: number; covered: number; missing: number };
	/** Ultimo giorno con la coppia garantita: fin dove arriva la previsione. */
	horizon: string | null;
	gaps: CurveHealthGapItem[];
	layers: CurveHealthLayerItem[];
}

/**
 * I due pezzi della garanzia che si possono rigenerare a mano.
 *
 * Sono due pulsanti e non uno perché sono due lavori con costi diversi di un
 * ordine di grandezza: l'anno indietro sono 365 giorni per punto di misura,
 * l'orizzonte otto. Un pulsante solo costringerebbe a pagare il primo ogni volta
 * che serve il secondo — che è il caso comune, perché la previsione è ciò che si
 * guarda e ciò che cambia ogni notte.
 */
export type CurveRegenScope = 'storico' | 'previsione';

/**
 * Gli zoom del grafico, dal più largo al più stretto.
 *
 * Sono quattro nomi e non un intervallo libero perché ognuno porta con sé la
 * **grana** con cui il server legge la curva: l'anno per giorno, il giorno per
 * quarto d'ora. Un anno a grana di quarto d'ora sarebbe trentaseimila punti per
 * livello, che nessun grafico disegna.
 */
export type CurveZoomKey = 'anno' | 'mese' | 'settimana' | 'giorno';

export const CURVE_ZOOM_LABELS: Record<CurveZoomKey, string> = {
	anno: 'Anno',
	mese: 'Mese',
	settimana: 'Settimana',
	giorno: 'Giorno',
};

/** La serie di un punto di misura, già nella forma che la chart legge. */
export interface CurveSeriesItem {
	meterId: string;
	points: PodLayerPoint[];
}

export interface CurveHealthPanelProps {
	/** `to` è escluso, come nella RPC. */
	window: { from: string; today: string; to: string };
	pastDays: number;
	futureDays: number;
	meters: CurveHealthMeterItem[];
	loading?: boolean;
	error?: string | null;
	/** Assente = niente pulsante: la scheda è di sola lettura. */
	onRefresh?: () => void;
	refreshing?: boolean;
	/** Assente = la scheda non rigenera, mostra soltanto. */
	onRegenerate?: (scope: CurveRegenScope) => void;
	/** Quale rigenerazione è in corso: disabilita entrambi i pulsanti. */
	regenerating?: CurveRegenScope | null;
	/** Esito dell'ultimo giro, da leggere accanto ai pulsanti. */
	regenerateNote?: string | null;
	/** L'ultimo giro è fallito: cambia il tono della nota, non il testo. */
	regenerateFailed?: boolean;

	/** Serie per punto di misura. Assente = niente grafico, solo i numeri. */
	series?: CurveSeriesItem[];
	zoom?: CurveZoomKey;
	onZoomChange?: (zoom: CurveZoomKey) => void;
	seriesLoading?: boolean;
}

const KIND_LABEL: Record<string, string> = {
	M1: 'Prelievo',
	M2: 'Immissione',
	M3: 'Accumulo',
};

const BASIS_LABEL: Record<string, string> = {
	dichiarato: 'consumo dichiarato dal socio',
	default_tipo: 'media per tipo di socio',
	impianti: 'kWp degli impianti censiti',
	default_kwp: 'potenza di riferimento',
};

const STATUS_TEXT: Record<CurveHealthStatus, { label: string; cls: string }> = {
	ok: { label: 'Completo', cls: 'border-accent-green/30 bg-accent-green/15 text-accent-green' },
	warn: { label: 'Da guardare', cls: 'border-warn/30 bg-warn/15 text-warn' },
	error: { label: 'Incompleto', cls: 'border-danger/30 bg-danger/15 text-danger' },
	exempt: { label: 'Non applicabile', cls: 'border-border bg-card text-text-muted' },
};

function StatusIcon({ status }: { status: CurveHealthStatus }) {
	if (status === 'ok') return <CheckCircle2 className="size-3.5" aria-hidden />;
	if (status === 'warn') return <AlertTriangle className="size-3.5" aria-hidden />;
	if (status === 'error') return <XCircle className="size-3.5" aria-hidden />;
	return <CircleSlash className="size-3.5" aria-hidden />;
}

function SeverityIcon({ severity }: { severity: CurveHealthSeverity }) {
	if (severity === 'error') return <XCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden />;
	if (severity === 'warn')
		return <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />;
	return <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />;
}

const SEVERITY_CLS: Record<CurveHealthSeverity, string> = {
	error: 'text-danger',
	warn: 'text-warn',
	info: 'text-text-muted',
};

const dateFmt = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short' });
const dateFmtLong = new Intl.DateTimeFormat('it-IT', {
	day: 'numeric',
	month: 'short',
	year: 'numeric',
});

/** `AAAA-MM-GG` come lo legge una persona. Mezzogiorno per non scivolare di fuso. */
function fmtDay(iso: string | null, long = false): string {
	if (!iso) return '-';
	const d = new Date(`${iso}T12:00:00Z`);
	return (long ? dateFmtLong : dateFmt).format(d);
}

function fmtKwh(v: number): string {
	return `${Math.round(v).toLocaleString('it-IT', IT_INT)} kWh`;
}

/**
 * La barra di copertura.
 *
 * Colorata per soglia e non per gradiente: la domanda è binaria — la garanzia è
 * rispettata o no — e un gradiente farebbe sembrare il 94% quasi verde quando
 * ventidue giorni dell'anno non hanno una curva.
 *
 * `aria-hidden` e non un `progressbar`: il conteggio esatto («355/365 giorni»)
 * sta già come testo nella riga sopra, quindi annunciarla di nuovo farebbe
 * leggere lo stesso numero due volte a chi usa uno screen reader.
 */
function CoverageBar({
	covered,
	total,
	tone,
}: {
	covered: number;
	total: number;
	tone: 'ok' | 'warn' | 'error';
}) {
	const pct = total > 0 ? Math.round((covered / total) * 100) : 0;
	const fill = tone === 'ok' ? 'bg-accent-green' : tone === 'warn' ? 'bg-warn' : 'bg-danger';
	return (
		<div className="h-1.5 w-full overflow-hidden rounded-[3px] bg-border" aria-hidden>
			<div className={`h-full rounded-[3px] ${fill}`} style={{ width: `${pct}%` }} />
		</div>
	);
}

/** Una delle due finestre della garanzia, con la sua regola in chiaro. */
function WindowRow({
	title,
	hint,
	covered,
	total,
	missing,
	exempt,
}: {
	title: string;
	hint: string;
	covered: number;
	total: number;
	missing: number;
	exempt: boolean;
}) {
	const tone = exempt || missing === 0 ? 'ok' : missing > total / 2 ? 'error' : 'warn';
	return (
		<div className="flex flex-col gap-1">
			<div className="flex flex-wrap items-baseline justify-between gap-x-2">
				<span className="font-semibold text-[12px] text-text-primary">{title}</span>
				<span className="font-mono text-[12px] text-text-muted tabular-nums">
					{exempt ? '-' : `${covered}/${total} giorni`}
				</span>
			</div>
			<CoverageBar covered={exempt ? 0 : covered} total={total} tone={tone} />
			<p className="text-[11px] text-text-muted">{hint}</p>
		</div>
	);
}

/** Una piastrella per livello: quanto copre, quanto vale, da dove viene. */
function LayerTile({ layer, exempt }: { layer: CurveHealthLayerItem; exempt: boolean }) {
	const days = layer.daysPast + layer.daysFuture;
	const slotPct =
		layer.slotsTotal > 0 ? Math.round((layer.slotsFilled / layer.slotsTotal) * 100) : 0;
	// Un livello scoperto resta visibile ma spento: la sua assenza è un dato, e
	// toglierlo dalla griglia lo farebbe sembrare non pertinente.
	return (
		<div
			className="flex flex-col gap-1 rounded-lg border border-border bg-card p-2.5"
			style={days === 0 ? { opacity: 0.55 } : undefined}
		>
			<div className="flex items-center gap-1.5">
				<span
					className="size-2 shrink-0 rounded-sm"
					style={{ backgroundColor: PodLayerColors[layer.name] }}
					aria-hidden
				/>
				<span className="truncate text-[11px] text-text-muted">
					{layer.rank} · {layer.label}
				</span>
			</div>
			<div className="font-mono font-semibold text-[13px] text-text-primary tabular-nums">
				{days === 0 ? 'assente' : `${days} gg`}
			</div>
			{days > 0 && (
				<>
					<div className="font-mono text-[11px] text-text-muted tabular-nums">
						{fmtKwh(layer.kwh)}
					</div>
					{slotPct < 100 && (
						<div className="text-[11px] text-warn tabular-nums">{slotPct}% degli slot</div>
					)}
					{layer.origins.length > 0 && (
						<div className="truncate text-[11px] text-text-muted">
							{layer.origins.map((o) => o.label).join(' · ')}
						</div>
					)}
					{layer.daysZero > 0 && (
						<div className="text-[11px] text-warn tabular-nums">{layer.daysZero} gg a zero</div>
					)}
					<div className="text-[11px] text-text-muted">
						{fmtDay(layer.firstDay)} → {fmtDay(layer.lastDay)}
					</div>
				</>
			)}
			{days === 0 && !exempt && (
				<div className="text-[11px] text-text-muted">nessun giorno nella finestra</div>
			)}
		</div>
	);
}

/** I buchi come intervalli: è così che si vanno a riparare. */
function GapList({ gaps }: { gaps: CurveHealthGapItem[] }) {
	// Oltre una dozzina la lista smette di essere una lista e diventa un muro:
	// i primi buchi bastano a capire la forma del problema, e il conteggio in
	// coda dice che non è tutto lì.
	const shown = gaps.slice(0, 12);
	const rest = gaps.length - shown.length;
	return (
		<div className="flex flex-col gap-1">
			<span className="font-semibold text-[12px] text-text-primary">
				Buchi nella coppia garantita
			</span>
			<ul className="flex flex-wrap gap-1.5">
				{shown.map((g) => (
					<li
						key={g.from}
						className="rounded-md border border-danger/25 bg-danger/10 px-1.5 py-0.5 font-mono text-[11px] text-danger tabular-nums"
					>
						{g.days === 1
							? fmtDay(g.from, true)
							: `${fmtDay(g.from)} → ${fmtDay(g.to, true)} · ${g.days} gg`}
					</li>
				))}
				{rest > 0 && <li className="px-1.5 py-0.5 text-[11px] text-text-muted">e altri {rest}</li>}
			</ul>
		</div>
	);
}

function MeterCard({
	meter,
	pastDays,
	futureDays,
	points,
	seriesLoading,
}: {
	meter: CurveHealthMeterItem;
	pastDays: number;
	futureDays: number;
	points?: PodLayerPoint[];
	seriesLoading?: boolean;
}) {
	const status = STATUS_TEXT[meter.status];
	const exempt = !meter.guaranteed;
	return (
		<section className="flex flex-col gap-3 rounded-xl border border-border bg-panel p-3.5">
			<header className="flex flex-wrap items-center justify-between gap-2">
				<div className="flex flex-wrap items-baseline gap-2">
					<span className="font-semibold text-[13px] text-text-primary">
						{meter.kind} · {KIND_LABEL[meter.kind] ?? meter.tipo}
					</span>
					<span className="font-mono text-[12px] text-text-muted">
						{meter.podCodice ?? 'POD non ancora inserito'}
					</span>
				</div>
				<span
					className={`inline-flex items-center gap-1 rounded-[11px] border px-1.5 py-0.5 font-semibold text-[11px] ${status.cls}`}
				>
					<StatusIcon status={meter.status} />
					{status.label}
				</span>
			</header>

			<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
				{meter.layers.map((l) => (
					<LayerTile key={l.rank} layer={l} exempt={exempt} />
				))}
			</div>

			{points && (
				<div className="flex flex-col gap-1">
					<span className="font-semibold text-[12px] text-text-primary">Andamento dei livelli</span>
					{points.length === 0 ? (
						<p className="text-[11px] text-text-muted">
							{seriesLoading
								? 'Caricamento…'
								: 'Nessun dato in questa finestra: è il difetto che le righe qui sopra contano.'}
						</p>
					) : (
						<div style={seriesLoading ? { opacity: 0.5 } : undefined}>
							<PodLayersChart data={points} height={150} hideLegend />
						</div>
					)}
				</div>
			)}

			<div className="grid gap-3 sm:grid-cols-2">
				<WindowRow
					title="Anno indietro"
					hint={
						exempt
							? 'Un punto di accumulo non ha curva tipica: non c’è niente da riempire.'
							: 'Da oggi−365 a ieri. Si riempie solo dove manca: un giorno passato che ha già la sua curva non cambia da solo.'
					}
					covered={meter.past.covered}
					total={pastDays}
					missing={meter.past.missing}
					exempt={exempt}
				/>
				<WindowRow
					title="Orizzonte di previsione"
					hint={
						exempt
							? 'Nessuna previsione su un punto di accumulo.'
							: `Da oggi a oggi+7, riscritto ogni notte perché cambiano le ancore. Arriva al ${fmtDay(meter.horizon, true)}.`
					}
					covered={meter.future.covered}
					total={futureDays}
					missing={meter.future.missing}
					exempt={exempt}
				/>
			</div>

			<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[12px]">
				<span className="font-semibold text-text-primary">Profilo GSE</span>
				{meter.profile ? (
					<>
						<span
							className={
								meter.profile.hasCoefficients === false
									? 'rounded-md border border-danger/30 bg-danger/10 px-1.5 py-0.5 font-mono font-semibold text-[11px] text-danger'
									: 'rounded-md border border-accent/30 bg-accent/10 px-1.5 py-0.5 font-mono font-semibold text-[11px] text-accent'
							}
						>
							{meter.profile.codice}
						</span>
						<span className="text-text-muted">
							{BASIS_LABEL[meter.profile.basis] ?? meter.profile.basis}
							{meter.profile.updatedAt
								? ` · aggiornato il ${fmtDay(meter.profile.updatedAt.slice(0, 10), true)}`
								: ''}
						</span>
					</>
				) : (
					<span className={exempt ? 'text-text-muted' : 'text-danger'}>
						{exempt
							? 'nessuno, per scelta: un M3 misura cariche e scariche, non deducibili senza telemetria'
							: 'assente - senza profilo la curva tipica non è generabile'}
					</span>
				)}
			</div>

			{meter.gaps.length > 0 && !exempt && <GapList gaps={meter.gaps} />}

			{meter.issues.length > 0 && (
				<ul className="flex flex-col gap-1.5">
					{meter.issues.map((i) => (
						<li key={i.code} className={`flex gap-1.5 text-[11px] ${SEVERITY_CLS[i.severity]}`}>
							<SeverityIcon severity={i.severity} />
							<span>{i.message}</span>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}

export function CurveHealthPanel({
	window: win,
	pastDays,
	futureDays,
	meters,
	loading,
	error,
	onRefresh,
	refreshing,
	onRegenerate,
	regenerating,
	regenerateNote,
	regenerateFailed,
	series,
	zoom = 'anno',
	onZoomChange,
	seriesLoading,
}: CurveHealthPanelProps) {
	if (loading) return <StatusBlock kind="loading" message="Verifica dei dati energetici…" />;
	if (error) return <StatusBlock kind="error" message={error} />;

	// Una rigenerazione in corso blocca **entrambi** i pulsanti e non solo il
	// suo: i due scrivono gli stessi giorni per gli stessi punti di misura, e
	// lanciarli insieme farebbe riscrivere all'uno ciò che l'altro sta leggendo
	// per calcolare le ancore.
	const busy = !!regenerating;

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-wrap items-baseline justify-between gap-2">
				<div className="flex flex-col gap-0.5">
					<span className="font-bold text-[13px] text-text-primary uppercase tracking-wider">
						Verifica dei dati energetici
					</span>
					<p className="text-[12px] text-text-muted">
						Finestra della garanzia: {fmtDay(win.from, true)} → {fmtDay(win.to, true)} - ogni punto
						di misura non-accumulo deve avere il livello tipico e quello stimato su tutti i{' '}
						{pastDays + futureDays} giorni.
					</p>
				</div>
				<div className="flex flex-wrap items-center gap-1.5">
					{onZoomChange && (
						<div className="mr-1 flex items-center gap-0.5 rounded-lg border border-border bg-card p-0.5">
							{(Object.keys(CURVE_ZOOM_LABELS) as CurveZoomKey[]).map((z) => (
								<button
									key={z}
									type="button"
									onClick={() => onZoomChange(z)}
									aria-pressed={zoom === z}
									className={
										zoom === z
											? 'rounded-md bg-accent px-2 py-0.5 font-semibold text-[11px] text-accent-foreground'
											: 'rounded-md px-2 py-0.5 text-[11px] text-text-muted hover:text-text-primary'
									}
								>
									{CURVE_ZOOM_LABELS[z]}
								</button>
							))}
						</div>
					)}
					{onRegenerate && (
						<>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => onRegenerate('storico')}
								disabled={busy || refreshing}
								title={`Riscrive i livelli tipico e stimato dal ${fmtDay(win.from, true)} a ieri: ${pastDays} giorni per punto di misura.`}
							>
								<History />
								{regenerating === 'storico' ? 'Rigenerazione…' : 'Rigenera dati storici'}
							</Button>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => onRegenerate('previsione')}
								disabled={busy || refreshing}
								title={`Riscrive i ${futureDays} giorni da oggi al ${fmtDay(win.to, true)}, ricalibrando la produzione sul meteo aggiornato.`}
							>
								<CalendarClock />
								{regenerating === 'previsione' ? 'Rigenerazione…' : 'Rigenera previsione'}
							</Button>
						</>
					)}
					{onRefresh && (
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={onRefresh}
							disabled={refreshing || busy}
						>
							<RefreshCw />
							{refreshing ? 'Verifica…' : 'Rileggi'}
						</Button>
					)}
				</div>
			</div>

			{regenerateNote && (
				<p className={`text-[12px] ${regenerateFailed ? 'text-danger' : 'text-text-muted'}`}>
					{regenerateNote}
				</p>
			)}

			{meters.length === 0 ? (
				<StatusBlock
					kind="empty"
					message="Nessun punto di misura su questo socio: i punti nascono dal ruolo, impostalo in Anagrafica."
				/>
			) : (
				meters.map((m) => (
					<MeterCard
						key={m.meterId}
						meter={m}
						pastDays={pastDays}
						futureDays={futureDays}
						points={series?.find((s2) => s2.meterId === m.meterId)?.points}
						seriesLoading={seriesLoading}
					/>
				))
			)}
		</div>
	);
}
